import { HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BusinessContextService } from '../state/business';
import { BusinessScopeEntry, BusinessScopingRegistry, BYPASS_BUSINESS_SCOPING } from './business-scoping-map';

/**
 * BusinessContextInterceptor — the scoping seam.
 *
 * For each outgoing API request that matches an enrolled scoping-map entry and
 * does not opt out, injects the active business id into the declared query
 * param or JSON body field. Registered ahead of RequestGrouping and Cache in
 * app.module so the injected id becomes part of their dedup/cache keys — a
 * response for the previous business can never satisfy a request for the new
 * one.
 *
 * It never overwrites a business filter the caller set explicitly, and passes
 * everything through when the environment kill switch is set.
 */
@Injectable()
export class BusinessContextInterceptor implements HttpInterceptor {
	constructor(private readonly registry: BusinessScopingRegistry, private readonly businessContext: BusinessContextService) {}

	public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (environment.disableBusinessContextInterceptor) {
			return next.handle(req);
		}
		if (req.context.get(BYPASS_BUSINESS_SCOPING)) {
			this.warnOnUnscopedMultiBusiness(req, true);
			return next.handle(req);
		}

		const entry = this.registry.match(req);
		if (!entry) {
			this.warnOnUnscopedMultiBusiness(req, false);
			return next.handle(req);
		}

		const activeId = this.businessContext.getActiveBusinessId();
		if (!activeId) {
			// Boot edge: no active business resolved yet. Pass through unmodified
			// rather than sending `businessId=undefined`.
			return next.handle(req);
		}

		return next.handle(this.injectBusinessId(req, entry, activeId));
	}

	private injectBusinessId(req: HttpRequest<unknown>, entry: BusinessScopeEntry, activeId: string): HttpRequest<unknown> {
		if (entry.inject === 'query') {
			// Treat an explicitly-set, non-empty param as caller intent; a missing
			// or empty param is injected. (Callers often omit the param entirely.)
			const existing = req.params.get(entry.field);
			if (existing != null && existing !== '') {
				return req;
			}
			return req.clone({ params: (req.params ?? new HttpParams()).set(entry.field, activeId) });
		}

		// body injection
		const body = req.body;
		if (body && typeof body === 'object' && !(body instanceof FormData)) {
			// The codebase commonly includes `businessId: value || undefined`, so a
			// present-but-nullish field still counts as unset and gets injected.
			const current = (body as Record<string, unknown>)[entry.field];
			if (current != null) {
				return req; // caller set it explicitly — do not overwrite
			}
			return req.clone({ body: { ...(body as Record<string, unknown>), [entry.field]: activeId } });
		}
		// No object body to inject into (null/FormData/primitive) — leave as-is.
		return req;
	}

	/**
	 * Dev-only guard against coverage gaps: warn when a request carries a
	 * multi-business filter (an array OR a comma-joined string) that was not
	 * explicitly opted out. Every such warning during QA should be treated as a
	 * defect — either the endpoint needs enrolling or the call needs the opt-out
	 * token.
	 */
	private warnOnUnscopedMultiBusiness(req: HttpRequest<unknown>, optedOut: boolean): void {
		if (!isDevMode() || optedOut) {
			return;
		}
		if (this.hasMultiBusinessFilter(req)) {
			// eslint-disable-next-line no-console
			console.warn(
				`[business-scoping] ${req.method} ${req.url} carries a multi-business filter without an opt-out token. ` +
					`Enroll the endpoint in the scoping map or mark the call cross-business.`
			);
		}
	}

	private hasMultiBusinessFilter(req: HttpRequest<unknown>): boolean {
		const queryValue = req.params.get('businessIds');
		if (queryValue && queryValue.indexOf(',') > -1) {
			return true;
		}
		const body = req.body as Record<string, unknown> | null;
		const bodyValue = body && typeof body === 'object' ? body['businessIds'] : undefined;
		if (Array.isArray(bodyValue) && bodyValue.length > 1) {
			return true;
		}
		return typeof bodyValue === 'string' && bodyValue.indexOf(',') > -1;
	}
}
